import { useTranslation } from 'react-i18next';
import { classes } from './ui.tsx';

/**
 * The three-step builder.
 *
 * Every quote opens on "How often?". Rooms and tasks share the middle step.
 * The last step is where the quote goes out.
 */
export const QUOTE_STEP_IDS = ['start', 'work', 'send'] as const;

export type QuoteStepId = (typeof QUOTE_STEP_IDS)[number];

export function quoteBuilderSteps(): Array<{ id: QuoteStepId; labelKey: string }> {
  return [
    { id: 'start', labelKey: 'quoteSteps.howOften' },
    { id: 'work', labelKey: 'quoteSteps.work' },
    { id: 'send', labelKey: 'quoteSteps.send' },
  ];
}

/**
 * Rendered as a real list of buttons rather than tabs: the steps can be visited
 * in any order once a quote exists, and `aria-current` announces where you are.
 */
export function QuoteStepper({
  steps,
  current,
  onSelect,
}: {
  steps: ReadonlyArray<{ id: QuoteStepId; labelKey: string }>;
  current: QuoteStepId;
  onSelect: (step: QuoteStepId) => void;
}) {
  const { t } = useTranslation();
  const currentIndex = steps.findIndex((step) => step.id === current);

  return (
    <nav aria-label={t('quoteSteps.ariaLabel')} className="mb-6">
      <ol className="flex flex-wrap gap-2">
        {steps.map((step, index) => {
          const isCurrent = step.id === current;
          const isVisited = index < currentIndex;
          return (
            <li key={step.id}>
              <button
                type="button"
                onClick={() => onSelect(step.id)}
                aria-current={isCurrent ? 'step' : undefined}
                className={classes(
                  'flex min-h-11 items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold',
                  isCurrent
                    ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand)] text-white'
                    : isVisited
                      ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                      : 'border-[color:var(--color-line)] bg-white text-[color:var(--color-muted)]',
                )}
              >
                <span
                  aria-hidden="true"
                  className={classes(
                    'flex h-6 w-6 items-center justify-center rounded-full border text-xs',
                    isCurrent ? 'border-white' : 'border-current',
                  )}
                >
                  {index + 1}
                </span>
                {t(step.labelKey)}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
